import React, { useState } from 'react';
import { defaultTheme } from '@styles/theme.styled';
import { RichTextBlock } from 'prismic-reactjs';
import generateId from '@helpers/generate-id';
import { ImageProps } from '@components/image';
import Text from '@components/text';
import { Wrapper } from '@components/wrapper.styled';
import Section from '@components/section';
import slugify from '@helpers/slugify';
import {
  TitleContainer,
  StyledCollapsible,
  OpenedContent,
  OpenedSubheading,
  AccordionItem,
  List,
  ListItem,
  StyledImage,
  StyledVector,
  Content,
  TextAndIcon,
  StyledText,
} from './listing.shared.styled';

const idGenerator = generateId();

interface ItemProps {
  title?: {
    text: string;
  };
  description?: {
    richText: RichTextBlock[];
  };
  image?: ImageProps;
}

export interface ListingProps {
  title?: string;
  items: ItemProps[];
  condensed?: boolean;
  backgroundColor?: keyof typeof defaultTheme.backgroundColors;
}

const Listing = ({
  title,
  items,
  backgroundColor = 'white',
  condensed = true,
}: ListingProps): JSX.Element => {
  const [opened, setOpened] = useState<null | number>(null);

  const renderTitleContent = (item: ItemProps, isOpened: boolean): JSX.Element => (
    <>
      <Content>
        {item.image?.url && (
          <StyledImage
            url={item.image?.url}
            gatsbyImageData={item.image?.gatsbyImageData}
            objectFit="contain"
            alt={item.image?.alt || ''}
          />
        )}
        <StyledText
          type="title"
          stringText={item.title?.text}
          fontSize="heading6"
          tag="h3"
          fontColor={condensed ? 'dark' : 'light'}
          role="heading"
        />
      </Content>
      <TextAndIcon>
        <StyledVector isRotated={isOpened} />
      </TextAndIcon>
    </>
  );

  return (
    <Section backgroundColor={backgroundColor}>
      <Wrapper>
        <TitleContainer>
          <Text
            tag="h2"
            type="title"
            stringText={title}
            fontSize="heading3"
            fontColor="secondary"
          />
        </TitleContainer>
        <List condensed={condensed}>
          {items.map((item, i) => {
            const itemId = idGenerator.next().value;
            const prefix = slugify(title || '');

            return (
              <ListItem key={itemId}>
                <StyledCollapsible
                  trigger={<AccordionItem>{renderTitleContent(item, false)}</AccordionItem>}
                  triggerElementProps={{
                    'aria-label': 'Open section',
                    tabIndex: 0,
                    role: 'button',
                    id: `${prefix}-trigger-${itemId}`,
                  }}
                  open={i === opened}
                  transitionTime={250}
                  easing="ease-out"
                  triggerWhenOpen={<OpenedContent>{renderTitleContent(item, true)}</OpenedContent>}
                  onOpening={() => setOpened(i)}
                  className="collapsible-item"
                  contentOuterClassName="collapsible-item-outer"
                  contentInnerClassName="collapsible-item-inner"
                  contentContainerTagName="div"
                >
                  <OpenedSubheading>
                    <Text
                      type="description"
                      richText={item.description?.richText}
                      fontColor={condensed ? 'dark' : 'light'}
                    />
                  </OpenedSubheading>
                </StyledCollapsible>
              </ListItem>
            );
          })}
        </List>
      </Wrapper>
    </Section>
  );
};

export default Listing;
